import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    TextField,
    MenuItem,
    Paper,
    useTheme
} from '@mui/material';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import Axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import { getAuthHeader } from '../../utils/authToken';
import { tokens } from '../../theme';
import Alert from '../../components/alert/Alert';

const NewsletterGeneration = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const signOut = useSignOut();
    const [searchParams] = useSearchParams();

    // ?type=admission|competition (default: competition)
    const newsletterType = searchParams.get('type') === 'admission' ? 'admission' : 'competition';
    const isAdmission = newsletterType === 'admission';

    const [loading, setLoading] = useState(false);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');

    // Alert
    const [successAlertOpen, setSuccessAlertOpen] = useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = useState('');
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = useState('');

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Newsletters');
    }, []);

    const handleGenerate = async () => {
        if (!month || !year) {
            setErrorAlertMessage('Please select both month and year');
            setErrorAlertOpen(true);
            return;
        }

        setGenerating(true);
        setError('');

        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/generate`,
                { month: parseInt(month), year: parseInt(year), type: newsletterType },
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('Newsletter generation started');
            setSuccessAlertOpen(true);

            // Redirect to progress page
            setTimeout(() => {
                navigate(`/admin/newsletters/progress/${response.data.uuid}`);
            }, 1000);
        } catch (error) {
            setGenerating(false);
            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 409) {
                setErrorAlertMessage('Newsletter already exists for this month and year');
                setErrorAlertOpen(true);
            } else {
                setErrorAlertMessage(
                    error.response?.data?.error || 'Failed to generate newsletter'
                );
                setErrorAlertOpen(true);
            }
        }
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header
                title={isAdmission ? 'Generate Admission Newsletter' : 'Generate Newsletter'}
                subtitle={isAdmission ? 'Top 3 US college admissions news (Korean)' : 'Create AI-Powered Monthly Newsletter'}
            />

            {loading ? (
                <Loading />
            ) : (
                <Paper
                    sx={{
                        p: 4,
                        maxWidth: 600,
                        mx: 'auto',
                        mt: 4,
                        backgroundColor: colors.primary[600]
                    }}
                >
                    <Typography variant="h5" mb={3} color={colors.grey[100]}>
                        Newsletter Details
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={3}>
                        <TextField
                            select
                            label="Month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            fullWidth
                            required
                        >
                            {monthNames.map((monthName, index) => (
                                <MenuItem key={index + 1} value={index + 1}>
                                    {monthName}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Year"
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            fullWidth
                            required
                        >
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: colors.primary[700],
                                borderRadius: 1,
                                border: `1px solid ${colors.primary[500]}`
                            }}
                        >
                            <Typography variant="body2" color={colors.grey[300]}>
                                <strong>Preview:</strong> {monthNames[month - 1]} {year} {isAdmission ? 'Admission Newsletter' : 'Newsletter'}
                            </Typography>
                            <Typography variant="caption" color={colors.grey[400]} mt={1} display="block">
                                {isAdmission
                                    ? `This will discover the Top 3 US college admissions news stories from the past 30 days and rewrite them in Korean for ${monthNames[month - 1]} ${year}.`
                                    : `This will generate an AI-powered newsletter featuring extracurricular competitions and activities for ${monthNames[month - 1]} ${year}.`}
                            </Typography>
                        </Box>

                        {error && (
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        )}

                        <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/admin/newsletters/${newsletterType}`)}
                                disabled={generating}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleGenerate}
                                disabled={generating}
                                sx={{
                                    backgroundColor: colors.primary[300],
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: colors.primary[400]
                                    }
                                }}
                            >
                                {generating ? 'Generating...' : 'Generate Newsletter'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical='bottom'
                horizontal='left'
            />
        </Box>
    );
};

export default NewsletterGeneration;
